/**
 * Warehouse Service
 * 
 * Warehouse service for handling warehouse operations and API communication including:
 * - Warehouse listing and management
 * - Warehouse statistics
 * - Warehouse CRUD operations
 * - Capacity management
 * - Location management
 * 
 * All methods use the centralized API client with JWT authentication, retry logic,
 * and offline queue support.
 * 
 * @module services/warehouseService
 */

import { api } from './api';
import { API_ENDPOINTS } from '../config/api.config.js';

export const warehouseService = {
  getWarehouses: async () => {
    try {
      const response = await api.get(`${API_ENDPOINTS.WAREHOUSES}`);
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.warehouses)) return data.warehouses;
      return [];
    } catch {
      
      return [];
    }
  },

  getStats: async () => {
    try {
      const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/status`);
      return data ?? {};
    } catch {
      
      return {};
    }
  },

  getWarehouseById: async (id) => {
    try {
      const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/${id}`);
      return data ?? null;
    } catch {
      
      return null;
    }
  },

  createWarehouse: async (warehouseData) => {
    const { data } = await api.post(`${API_ENDPOINTS.WAREHOUSES}`, warehouseData);
    return data ?? {};
  },

  updateWarehouse: async (id, warehouseData) => {
    const { data } = await api.put(`${API_ENDPOINTS.WAREHOUSES}/${id}`, warehouseData);
    return data ?? {};
  },

  deleteWarehouse: async (id) => {
    const { data } = await api.delete(`${API_ENDPOINTS.WAREHOUSES}/${id}`);
    return data ?? {};
  },

  getCapacity: async (warehouseId) => {
    try {
      const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/${warehouseId}/capacity`);
      return data ?? {};
    } catch {
      
      return {};
    }
  },

  getLocations: async (warehouseId) => {
    try {
      const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/${warehouseId}/locations`);
      return data ?? [];
    } catch {
      
      return [];
    }
  },

  async getReceivingSchedule() {
    try {
      const response = await api.get(`${API_ENDPOINTS.RECEIVING}/orders`);
      return response;
    } catch {
      
      return { data: [] };
    }
  },

  async getLocationStatus() {
    try {
      const response = await api.get(`${API_ENDPOINTS.INVENTORY}/items`);
      return response;
    } catch {
      
      return { data: [] };
    }
  },

  async getZoneAlerts() {
    try {
      const { data } = await api.get(`${API_ENDPOINTS.WAREHOUSES}/alerts`);
      const alerts = Array.isArray(data) ? data : (Array.isArray(data?.alerts) ? data.alerts : []);
      return { alerts };
    } catch {
      
      return { alerts: [] };
    }
  }
};

export default warehouseService;
